import { useHeaderHeight } from "expo-router/react-navigation";
import { PropsWithChildren } from 'react';
import { Dimensions, KeyboardAvoidingView, Platform, StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import DismissKeyboard from '../DismissKeyboard';

const { height } = Dimensions.get('window');

type Props = PropsWithChildren<{
  scrollEnable?: boolean;
  keyboardVerticalOffset?: number;
  horizontalPadding?: number;
  minHeightMinus?: number;
  behavior?: 'padding' | 'position' | 'height';
  bounces?: boolean;
}>;

const FormWrapper = ({
  children,
  scrollEnable = true,
  keyboardVerticalOffset,
  horizontalPadding = 18,
  minHeightMinus = 0,
  behavior = 'padding',
  bounces = true
}: Props) => {
  const headerHeight = useHeaderHeight();
  const topInsets = useSafeAreaInsets().top;

  if (Platform.OS === 'web') {
    return <View style={screenStyles.webContainer}>{children}</View>;
  }

  return (
    <View style={screenStyles.container}>
      {Platform.OS === 'ios' ? (
        <KeyboardAvoidingView
          behavior={behavior}
          keyboardVerticalOffset={keyboardVerticalOffset ?? headerHeight}
          style={screenStyles.keyboard}>
          <DismissKeyboard>
            {scrollEnable ? (
              <ScrollView
                keyboardShouldPersistTaps="handled"
                bounces={bounces}
                contentContainerStyle={[
                  { ...screenStyles.scroll, paddingHorizontal: horizontalPadding },
                  { minHeight: height - topInsets - headerHeight - minHeightMinus }
                ]}
                showsVerticalScrollIndicator={false}>
                {children}
              </ScrollView>
            ) : (
              <View style={{ ...screenStyles.view, paddingHorizontal: horizontalPadding }}>{children}</View>
            )}
          </DismissKeyboard>
        </KeyboardAvoidingView>
      ) : scrollEnable ? (
        <ScrollView
          keyboardShouldPersistTaps="handled"
          style={screenStyles.scrollContainer}
          contentContainerStyle={[
            { ...screenStyles.scroll, paddingHorizontal: horizontalPadding },
            { minHeight: height - topInsets - headerHeight - minHeightMinus }
          ]}
          showsVerticalScrollIndicator={false}>
          {children}
        </ScrollView>
      ) : (
        <View style={{ ...screenStyles.view, paddingHorizontal: horizontalPadding }}>{children}</View>
      )}
    </View>
  );
};

export default FormWrapper;

const screenStyles = StyleSheet.create({
  webContainer: {
    flex: 1,
    paddingBottom: 25,
    height: '100%',
    width: '100%'
  },

  container: {
    flex: 1,
    alignItems: 'center',
    width: '100%'
  },

  keyboard: {
    width: '100%',
    flex: 1
  },

  scrollContainer: {
    width: '100%'
  },

  scroll: {
    flexGrow: 1
  },

  view: {
    flex: 1,
    width: '100%'
  }
});
